import React, { useMemo, useState } from "react";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function toDate(value) {
  if (!value) return null;

  try {
    const parsed =
      typeof value?.toDate === "function"
        ? value.toDate()
        : new Date(value);

    return Number.isNaN(parsed.getTime())
      ? null
      : parsed;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const parsed = toDate(value);

  return parsed
    ? parsed.toLocaleString()
    : "Date unavailable";
}

function titleCase(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function getCommunicationDate(record) {
  return (
    record?.sentAt ||
    record?.deliveredAt ||
    record?.createdAt ||
    record?.updatedAt
  );
}

function getChannel(record) {
  return normalize(
    record?.channel ||
      record?.communicationType ||
      record?.type ||
      "in_app"
  );
}

function getStatus(record) {
  return normalize(
    record?.deliveryStatus ||
      record?.status ||
      "recorded"
  );
}

function getChannelTone(channel) {
  if (channel === "email") {
    return {
      color: "#1d4ed8",
      background: "#dbeafe",
    };
  }

  if (["sms", "text"].includes(channel)) {
    return {
      color: "#166534",
      background: "#dcfce7",
    };
  }

  if (channel === "phone") {
    return {
      color: "#7c3aed",
      background: "#ede9fe",
    };
  }

  if (
    ["broadcast", "notification"].includes(channel)
  ) {
    return {
      color: "#92400e",
      background: "#fef3c7",
    };
  }

  return {
    color: "#475569",
    background: "#f1f5f9",
  };
}

function getStatusTone(status) {
  if (
    ["sent", "delivered", "read", "recorded"].includes(
      status
    )
  ) {
    return {
      color: "#166534",
      background: "#dcfce7",
    };
  }

  if (
    ["failed", "undelivered", "rejected"].includes(
      status
    )
  ) {
    return {
      color: "#b91c1c",
      background: "#fee2e2",
    };
  }

  return {
    color: "#92400e",
    background: "#fef3c7",
  };
}

export default function Facility360Communications({
  facility,
  communications = [],
}) {
  const [channelFilter, setChannelFilter] =
    useState("all");

 const facilityId = normalize(
  facility?.facilityId || facility?.id
);

const facilityName = normalize(
  facility?.facilityName || facility?.name
);

const tenantId = normalize(
  facility?.tenantId ||
    facility?.tenantKey
);

  const facilityCommunications = useMemo(() => {
    const rows = Array.isArray(communications)
      ? communications
      : [];

    return rows
.filter((record) => {
  const recordFacilityId = normalize(
    record?.facilityId ||
      record?.assignedFacilityId ||
      record?.recipientFacilityId
  );

  const recordFacilityName = normalize(
    record?.facilityName ||
      record?.recipientFacilityName
  );

  const recordTenantId = normalize(
    record?.tenantId ||
      record?.tenantKey
  );

  if (
    tenantId &&
    recordTenantId &&
    recordTenantId !== tenantId
  ) {
    return false;
  }

  if (facilityId && recordFacilityId) {
    return recordFacilityId === facilityId;
  }

  return Boolean(
    facilityName &&
      !recordFacilityId &&
      recordFacilityName === facilityName
  );
})
      .sort((a, b) => {
        const aDate = toDate(
          getCommunicationDate(a)
        );

        const bDate = toDate(
          getCommunicationDate(b)
        );

        return (
          (bDate?.getTime() || 0) -
          (aDate?.getTime() || 0)
        );
      });
  }, [
  communications,
  facilityId,
  facilityName,
  tenantId,
]);

  const channels = useMemo(
    () =>
      Array.from(
        new Set(
          facilityCommunications
            .map(getChannel)
            .filter(Boolean)
        )
      ),
    [facilityCommunications]
  );

  const visibleCommunications = useMemo(() => {
    if (channelFilter === "all") {
      return facilityCommunications;
    }

    return facilityCommunications.filter(
      (record) =>
        getChannel(record) === channelFilter
    );
  }, [facilityCommunications, channelFilter]);

  const deliveredCount =
    facilityCommunications.filter((record) =>
      ["sent", "delivered", "read", "recorded"].includes(
        getStatus(record)
      )
    ).length;

  const failedCount =
    facilityCommunications.filter((record) =>
      ["failed", "undelivered", "rejected"].includes(
        getStatus(record)
      )
    ).length;

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Communication History
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Email, SMS, phone, notification, and
          operational communication records.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(3, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        {[
          [
            "Communications",
            facilityCommunications.length,
            "#1d4ed8",
          ],
          ["Successful", deliveredCount, "#166534"],
          [
            "Failed",
            failedCount,
            failedCount > 0
              ? "#b91c1c"
              : "#166534",
          ],
        ].map(([label, value, color]) => (
          <div
            key={label}
            style={{
              padding: 13,
              borderRadius: 14,
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
            }}
          >
            <div
              style={{
                color: "#64748b",
                fontSize: 10,
                fontWeight: 900,
                textTransform: "uppercase",
              }}
            >
              {label}
            </div>

            <div
              style={{
                marginTop: 5,
                color,
                fontSize: 22,
                fontWeight: 950,
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          gap: 7,
          overflowX: "auto",
          paddingBottom: 3,
        }}
      >
        <button
          type="button"
          onClick={() => setChannelFilter("all")}
          style={{
            minHeight: 34,
            padding: "6px 11px",
            borderRadius: 999,
            border:
              channelFilter === "all"
                ? "1px solid #2563eb"
                : "1px solid #cbd5e1",
            background:
              channelFilter === "all"
                ? "#2563eb"
                : "#ffffff",
            color:
              channelFilter === "all"
                ? "#ffffff"
                : "#334155",
            cursor: "pointer",
            fontSize: 10,
            fontWeight: 900,
            whiteSpace: "nowrap",
          }}
        >
          All
        </button>

        {channels.map((channel) => (
          <button
            key={channel}
            type="button"
            onClick={() =>
              setChannelFilter(channel)
            }
            style={{
              minHeight: 34,
              padding: "6px 11px",
              borderRadius: 999,
              border:
                channelFilter === channel
                  ? "1px solid #2563eb"
                  : "1px solid #cbd5e1",
              background:
                channelFilter === channel
                  ? "#2563eb"
                  : "#ffffff",
              color:
                channelFilter === channel
                  ? "#ffffff"
                  : "#334155",
              cursor: "pointer",
              fontSize: 10,
              fontWeight: 900,
              whiteSpace: "nowrap",
            }}
          >
            {titleCase(channel)}
          </button>
        ))}
      </div>

      {visibleCommunications.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
            color: "#64748b",
            textAlign: "center",
            fontWeight: 750,
          }}
        >
          No communication records were found for this
          facility.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {visibleCommunications.map(
            (record, index) => {
              const channel = getChannel(record);
              const status = getStatus(record);
              const channelTone =
                getChannelTone(channel);
              const statusTone =
                getStatusTone(status);

              const subject =
                record?.subject ||
                record?.title ||
                record?.templateName ||
                `${titleCase(channel)} Communication`;

              const message =
                record?.message ||
                record?.body ||
                record?.content ||
                record?.summary ||
                "";

              const recipient =
                record?.recipientName ||
                record?.recipientEmail ||
                record?.recipientPhone ||
                record?.to ||
                "Facility contact";

              const sender =
                record?.senderName ||
                record?.createdByName ||
                record?.from ||
                "ShiftBridge";

              return (
                <div
                  key={
                    record?.id ||
                    `communication-${index}`
                  }
                  style={{
                    padding: 14,
                    borderRadius: 15,
                    border: "1px solid #e2e8f0",
                    background: "#ffffff",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent:
                        "space-between",
                      alignItems: "flex-start",
                      gap: 10,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#0f172a",
                          fontSize: 14,
                          fontWeight: 950,
                        }}
                      >
                        {String(subject)}
                      </div>

                      <div
                        style={{
                          marginTop: 4,
                          color: "#64748b",
                          fontSize: 10,
                          fontWeight: 750,
                        }}
                      >
                        {formatDateTime(
                          getCommunicationDate(record)
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                      }}
                    >
                      <span
                        style={{
                          padding: "5px 8px",
                          borderRadius: 999,
                          color: channelTone.color,
                          background:
                            channelTone.background,
                          fontSize: 9,
                          fontWeight: 950,
                          textTransform: "uppercase",
                        }}
                      >
                        {titleCase(channel)}
                      </span>

                      <span
                        style={{
                          padding: "5px 8px",
                          borderRadius: 999,
                          color: statusTone.color,
                          background:
                            statusTone.background,
                          fontSize: 9,
                          fontWeight: 950,
                          textTransform: "uppercase",
                        }}
                      >
                        {titleCase(status)}
                      </span>
                    </div>
                  </div>

                  {message ? (
                    <div
                      style={{
                        marginTop: 10,
                        color: "#475569",
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {String(message)}
                    </div>
                  ) : null}

                  <div
                    style={{
                      display: "grid",
                      gap: 3,
                      marginTop: 10,
                      color: "#64748b",
                      fontSize: 10,
                      fontWeight: 750,
                    }}
                  >
                    <div>
                      From: {String(sender)}
                    </div>

                    <div>
                      To: {String(recipient)}
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}